import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { SETTINGS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Sparkles, ShieldCheck, Server, WifiOff } from "lucide-react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [st, ag] = await Promise.all([api.settings(), api.listAgents()]);
        setS(st); setAgents(ag);
      } catch { toast.error(t.common.failed); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    if (!s) return;
    const patch = { ...s };
    delete patch.id;
    try {
      const updated = await api.updateSettings(patch);
      setS(updated);
      toast.success(t.common.saved);
    } catch { toast.error(t.common.saveFailed); }
  };

  const testAi = async () => {
    setPinging(true); setPingResult(null);
    try {
      const r = await api.aiPing();
      setPingResult(r);
      if (r.ok) toast.success("ИИ доступен");
      else toast.error("Ошибка ИИ: " + r.error);
    } finally { setPinging(false); }
  };

  if (loading || !s) return <div className="text-slate-400 text-sm">{t.common.loading}</div>;

  return (
    <div data-testid={SETTINGS.root}>
      <PageHeader
        title={t.settings.title}
        description={t.settings.description}
        actions={
          <button
            data-testid={SETTINGS.saveBtn}
            onClick={save}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
          >
            <Save className="h-4 w-4" /> {t.settings.save}
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-soft p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 text-emerald-500" />
            <div className="font-display font-semibold text-slate-900">{t.settings.aiModels}</div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.settings.fields.aiProvider}</Label>
              <Input value={s.ai_provider} onChange={(e) => setS({ ...s, ai_provider: e.target.value })} className="mt-1 font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.settings.fields.claudeModel}</Label>
              <Input value={s.claude_model} onChange={(e) => setS({ ...s, claude_model: e.target.value })} className="mt-1 font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.settings.fields.geminiModel}</Label>
              <Input value={s.gemini_model} onChange={(e) => setS({ ...s, gemini_model: e.target.value })} className="mt-1 font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.settings.fields.budget}</Label>
              <Input type="number" value={s.budget_monthly_usd} onChange={(e) => setS({ ...s, budget_monthly_usd: Number(e.target.value) })} className="mt-1" />
            </div>
            <button
              data-testid={SETTINGS.aiPingBtn}
              onClick={testAi}
              disabled={pinging}
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium disabled:opacity-50 transition-colors"
            >
              <ShieldCheck className="h-4 w-4" />
              {pinging ? t.settings.pinging : t.settings.testAi}
            </button>
            {pingResult && (
              <div className={`mt-3 text-xs px-3 py-2 rounded-lg font-mono ${
                pingResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
              }`}>
                {pingResult.ok ? t.settings.aiOk(pingResult.model, pingResult.response) : t.settings.aiError(pingResult.error)}
              </div>
            )}
          </div>
        </div>

        <div className="card-soft p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            <div className="font-display font-semibold text-slate-900">{t.settings.antidetect}</div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.settings.fields.proxy}</Label>
              <Select value={s.proxy_provider} onValueChange={(v) => setS({ ...s, proxy_provider: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="iProxy">iProxy</SelectItem>
                  <SelectItem value="Astroproxy">Astroproxy</SelectItem>
                  <SelectItem value="LTESocks">LTESocks</SelectItem>
                  <SelectItem value="eSIM">eSIM (реальная симка)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.settings.fields.geo}</Label>
              <Select value={s.default_geo} onValueChange={(v) => setS({ ...s, default_geo: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">{t.geoOptions.US}</SelectItem>
                  <SelectItem value="UK">{t.geoOptions.UK}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t.settings.fields.quietStart}</Label>
                <Input type="number" min={0} max={23} value={s.quiet_hours_start} onChange={(e) => setS({ ...s, quiet_hours_start: Number(e.target.value) })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t.settings.fields.quietEnd}</Label>
                <Input type="number" min={0} max={23} value={s.quiet_hours_end} onChange={(e) => setS({ ...s, quiet_hours_end: Number(e.target.value) })} className="mt-1" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-soft p-6 mt-5">
        <div className="flex items-center gap-2 mb-4">
          <Server className="h-4 w-4 text-emerald-500" />
          <div className="font-display font-semibold text-slate-900">{t.settings.macAgents}</div>
        </div>
        {agents.length === 0 ? (
          <div className="flex items-start gap-2 text-sm text-slate-500">
            <WifiOff className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{t.settings.noAgents} <span className="text-slate-400">{t.settings.agentHint}</span></span>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {agents.map((a) => (
              <div key={a.agent_id} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <div className="font-medium text-slate-900">{a.agent_id}</div>
                  <div className="text-xs text-slate-500 font-mono">{a.hostname} · v{a.version}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">{t.settings.lastSeen}</div>
                  <div className="text-xs font-mono text-slate-700">{new Date(a.last_seen).toLocaleString("ru-RU")}</div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                  {t.settings.devicesCount(a.devices_reported)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-soft p-6 mt-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t.settings.session}</div>
        <div className="text-sm text-slate-700">
          {t.settings.sessionText("ADMIN_PIN")}
        </div>
      </div>
    </div>
  );
}
